import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaEdit,
  FaCheckCircle,
  FaTimesCircle,
  FaSync,
  FaSave,
  FaTimes,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import { fetchTenantInfo, updateTenantInfo } from '../../store/slices/tenantSlice';
import './TenantInfoPage.css';
import { Loading } from '../../components/common';

const availableApps = [
  "Microsoft Word",
  "Microsoft Excel",
  "Microsoft PowerPoint",
  "Microsoft Outlook",
  "Microsoft Teams",
  "OneDrive",
  "Microsoft Edge",
  "Microsoft Copilot"
];

const TenantInfo = () => {
  const dispatch = useDispatch();
  const { data, loading: tenantLoading, error: tenantError } = useSelector((state) => state.tenant);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    group_check: false,
    revoke_session: false,
    itdr_risk_score: 0,
    session_check_group: '',
    mails: [],
    applications: [],
    validTime: 0,
  });
  const [newEmail, setNewEmail] = useState('');
  const [selectedApp, setSelectedApp] = useState("");


  // Initial load for both APIs
  useEffect(() => {
    dispatch(fetchTenantInfo());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      setFormData({
        group_check: data.group_check || false,
        revoke_session: data.revoke_session || false,
        itdr_risk_score: data.itdr_risk_score || 0,
        session_check_group: data.session_check_group || '',
        mails: data.mails || [],
        applications:data.applications ||[],
        validTime:data.validTime || 0,
      });
    }
  }, [data]);

  const handleRefresh = () => {
    dispatch(fetchTenantInfo());
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      setFormData({
        group_check: data?.group_check || false,
        revoke_session: data?.revoke_session || false,
        itdr_risk_score: data?.itdr_risk_score || 0,
        session_check_group: data?.session_check_group || '',
        mails: data?.mails || [],
        applications:data?.applications || [],
        validTime:data.validTime || 0,
      });
    }
    setIsEditMode(!isEditMode);
  };

  const handleSave = () => {
    dispatch(updateTenantInfo(formData)).then((result) => {
      if (result.payload?.success) {
        setIsEditMode(false);
        dispatch(fetchTenantInfo());
        // console.log('Updated successfully');
      } else {
        // console.log('Update failed');
      }
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddEmail = () => {
    if (newEmail && !formData.mails.includes(newEmail)) {
      setFormData(prev => ({
        ...prev,
        mails: [...prev.mails, newEmail]
      }));
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      mails: prev.mails.filter(email => email !== emailToRemove)
    }));
  };

  const handleAddApplication = () => {
    if (
      selectedApp &&
      !formData.applications.includes(selectedApp)
    ) {
      setFormData((prev) => ({
        ...prev,
        applications: [...prev.applications, selectedApp]
      }));
      setSelectedApp("");
    }
  };

  const handleRemoveApplication = (app) => {
    setFormData((prev) => ({
      ...prev,
      applications: prev.applications.filter((a) => a !== app)
    }));
  };

  if (tenantLoading) {
    return (
      <div className="tenant-info-page">
        <div className="tenant-info__custom-header">
          <div className="tenant-info__header-titles">
            <h1>Tenant Info</h1>
            <p>ITDR Tenant Configuration</p>
          </div>
        </div>
          <Loading text='Loading tenant information...'/>
      </div>
    );
  }

  if (tenantError) {
    const displayError = typeof tenantError === 'object' ? (tenantError.error || "Server Error") : tenantError;
    return (
      <div className="tenant-info-page">
        <div className="tenant-info__custom-header">
          <div className="tenant-info__header-titles">
            <h1>Tenant Info</h1>
            <p>ITDR Tenant Configuration</p>
          </div>
        </div>
        <div className="tenant-info__error-state">
          <FaTimesCircle className="tenant-info__error-icon" />
          <p>{displayError}</p>
          <button onClick={handleRefresh} className="tenant-info__retry-btn">
            <FaSync /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-info-page">
      {/* Custom Header */}
      {/* <div className="tenant-info__custom-header">
        <div className="tenant-info__header-titles">
          <h1>Tenant Info</h1>
          <p>ITDR Tenant Configuration</p>
        </div>  
      </div> */}
 
      {/* Vertical Stack Layout - Domain inventory card is now placed directly underneath */}
      <div className="tenant-info__stacked-layout" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* Tenant Configuration Card */}
        <div className="tenant-info__config-card">
          <div className="tenant-info__config-header">
            <div className="tenant-info__header-left">
              <h2>Tenant Configuration</h2>
            </div>
            <div className="tenant-info__page-actions">
          <button 
            className="tenant-info__action-btn tenant-info__action-btn--secondary" 
            onClick={handleRefresh}
            disabled={isEditMode}
          >
            <FaSync /> Refresh
          </button>
          
          {!isEditMode ? (
            <button 
              className="tenant-info__action-btn tenant-info__action-btn--primary" 
              onClick={handleEditToggle}
            >
              <FaEdit /> Edit Configuration
            </button>
          ) : (
            <>
              <button 
                className="tenant-info__action-btn tenant-info__action-btn--cancel" 
                onClick={handleEditToggle}
              >
                <FaTimes /> Cancel
              </button>
              <button 
                className="tenant-info__action-btn tenant-info__action-btn--save" 
                onClick={handleSave}
              >
                <FaSave /> Save Changes
              </button>
            </>
          )}
        </div>
          </div>

          <div className="tenant-info__config-body">
            <table className="tenant-info__config-table">
              <thead>
                <tr>
                  <th>Configuration</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {/* Revoke Session */}
                <tr>
                  <td>
                    <div className="tenant-info__config-label">
                      <span className="tenant-info__label-text">Revoke Session</span>
                      <span className="tenant-info__label-desc">Automatic session revocation</span>
                    </div>
                  </td>
                  <td>
                    {isEditMode ? (
                      <label className="tenant-info__toggle-switch">
                        <input
                          type="checkbox"
                          checked={formData.revoke_session}
                          onChange={(e) => handleInputChange('revoke_session', e.target.checked)}
                        />
                        <span className="tenant-info__toggle-slider"></span>
                      </label>
                    ) : (
                      <span className={`tenant-info__status-badge ${formData.revoke_session ? 'tenant-info__status-badge--active' : 'tenant-info__status-badge--inactive'}`}>
                        {formData.revoke_session ? (
                          <><FaCheckCircle /> Enabled</>
                        ) : (
                          <><FaTimesCircle /> Disabled</>
                        )}
                      </span>
                    )}
                  </td>
                </tr>

                {/* Risk Score */}
                <tr>
                  <td>
                    <div className="tenant-info__config-label">
                      <span className="tenant-info__label-text">ITDR Risk Score</span>
                      <span className="tenant-info__label-desc">Current configured tenant risk threshold (0-100)</span>
                    </div>
                  </td>
                  <td>
                    {isEditMode ? (
                      <div className="tenant-info__input-group">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          className="tenant-info__input tenant-info__input--number"
                          value={formData.itdr_risk_score}
                          onChange={(e) => handleInputChange('itdr_risk_score', parseInt(e.target?.value))}
                        />
                        <span className="tenant-info__input-suffix">/ 100</span>
                      </div>
                    ) : (
                      <div className="tenant-info__risk-display">
                        <span className="tenant-info__config-value tenant-info__config-value--risk">
                          {formData.itdr_risk_score}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>

                {/* Group Check */}
                <tr>
                  <td>
                    <div className="tenant-info__config-label">
                      <span className="tenant-info__label-text">Group Check</span>
                      <span className="tenant-info__label-desc">Enable group-based validation</span>
                    </div>
                  </td>
                  <td>
                    {isEditMode ? (
                      <label className="tenant-info__toggle-switch">
                        <input
                          type="checkbox"
                          checked={formData.group_check}
                          onChange={(e) => handleInputChange('group_check', e.target.checked)}
                        />
                        <span className="tenant-info__toggle-slider"></span>
                      </label>
                    ) : (
                      <span className={`tenant-info__status-badge ${formData.group_check ? 'tenant-info__status-badge--active' : 'tenant-info__status-badge--inactive'}`}>
                        {formData.group_check ? (
                          <><FaCheckCircle /> Enabled</>
                        ) : (
                          <><FaTimesCircle /> Disabled</>
                        )}
                      </span>
                    )}
                  </td>
                </tr>

                {/* Session Check Group */}
                <tr>
                  <td>
                    <div className="tenant-info__config-label">
                      <span className="tenant-info__label-text">Session Check Group</span>
                      <span className="tenant-info__label-desc">Group identifier for session validation</span>
                    </div>
                  </td>
                  <td>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="tenant-info__input tenant-info__input--text"
                        value={formData.session_check_group}
                        onChange={(e) => handleInputChange('session_check_group', e.target.value)}
                        placeholder="Enter group name"
                      />
                    ) : (
                      <span className="tenant-info__config-value tenant-info__config-value--code">
                        {formData.session_check_group || 'Not configured'}
                      </span>
                    )}
                  </td>
                </tr>

                {/* Notification Emails */}
                <tr>
                  <td>
                    <div className="tenant-info__config-label">
                      <span className="tenant-info__label-text">Notification Emails</span>
                      <span className="tenant-info__label-desc">Email addresses for alerts</span>
                    </div>
                  </td>
                  <td>
                    {isEditMode ? (
                      <div className="tenant-info__email-edit">
                        <div className="tenant-info__email-input-group">
                          <input
                            type="email"
                            className="tenant-info__input tenant-info__input--email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="Enter email address"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                          />
                          <button 
                            className="tenant-info__add-email-btn"
                            onClick={handleAddEmail}
                            type="button"
                          >
                            <FaPlus /> Add
                          </button>
                        </div>
                        <div className="tenant-info__email-list">
                          {formData.mails.map((email, index) => (
                            <div key={index} className="tenant-info__email-item tenant-info__email-item--edit">
                              <span>{email}</span>
                              <button
                                className="tenant-info__remove-email-btn"
                                onClick={() => handleRemoveEmail(email)}
                                type="button"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="tenant-info__email-tags">
                        {formData?.mails && formData.mails.length > 0 ? (
                          formData?.mails.map((email, index) => (
                            <span key={index} className="tenant-info__email-tag">
                              {email}
                            </span>
                          ))
                        ) : (
                          <span className="tenant-info__config-value tenant-info__config-value--empty">
                            No emails configured
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="tenant-info__config-label">
                      <span className="tenant-info__label-text">Applications</span>
                      <span className="tenant-info__label-desc">
                        Configure allowed applications
                      </span>
                    </div>
                  </td>

                  <td>
                    {isEditMode ? (
                      <div className="tenant-info__app-edit">
                        <div className="tenant-info__app-input-group">
                          <select
                            className="tenant-info__select"
                            value={selectedApp}
                            onChange={(e) => setSelectedApp(e.target?.value)}
                          >
                            <option value="">Select Application</option>

                            {availableApps
                              .filter(
                                (app) => !formData?.applications?.includes(app)
                              )
                              .map((app) => (
                                <option key={app} value={app}>
                                  {app}
                                </option>
                              ))}
                          </select>

                          <button
                            className="tenant-info__add-app-btn"
                            onClick={handleAddApplication}
                            type="button"
                          >
                            <FaPlus /> Add
                          </button>
                        </div>

                        <div className="tenant-info__app-list">
                          {formData?.applications?.map((app) => (
                            <div
                              key={app}
                              className="tenant-info__app-item tenant-info__app-item--edit"
                            >
                              <span>{app}</span>

                              <button
                                className="tenant-info__remove-app-btn"
                                onClick={() => handleRemoveApplication(app)}
                                type="button"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="tenant-info__app-tags">
                        {formData?.applications?.length > 0 ? (
                          formData?.applications.map((app) => (
                            <span key={app} className="tenant-info__app-tag">
                              {app}
                            </span>
                          ))
                        ) : (
                          <span className="tenant-info__config-value tenant-info__config-value--empty">
                            No applications configured
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {/* Valid Time */}
                <tr>
                  <td>
                    <div className="tenant-info__config-label">
                      <span className="tenant-info__label-text">Valid Time</span>
                      <span className="tenant-info__label-desc">Current configured Valid Time for session in seconds</span>
                    </div>
                  </td>
                  <td>
                    {isEditMode ? (
                      <div className="tenant-info__input-group">
                        <input
                          type="number"
                          min="0"
                          // max="100"
                          className="tenant-info__input tenant-info__input--number"
                          value={formData?.validTime}
                          onChange={(e) => handleInputChange('validTime', parseInt(e.target?.value))}
                        />
                        {/* <span className="tenant-info__input-suffix">/ 100</span> */}
                      </div>
                    ) : (
                      <div className="tenant-info__risk-display">
                        <span className="tenant-info__config-value tenant-info__config-value--risk">
                          {formData?.validTime}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>

              </tbody>
            </table>
          </div>
        </div>


      </div>
    </div>
  );
};

export default TenantInfo;